import { Estudiante } from "src/app/models/estudiante.model";
import { Graduacion } from "src/app/models/graduacion.model";
import { MaderaTipo } from "./precios-config.model";

export interface MaderaSeleccionada {
  tipo: MaderaTipo;
  cantidad: number;
}

export interface PagoEstudiante {
  incluyeExamen: boolean;
  incluyeLibreta: boolean;
  maderasSeleccionadas: MaderaSeleccionada[];
  precioIndumentaria: number;
  indumentariaDescripcion?: string;
  descuentoPorcentaje: number;
  descuentoValor: number;
  descExamenPct: number;
  descLibretaPct: number;
  descMaderasPct: number;
  descIndumentariaPct: number;
  valorExamen: number;
  valorLibreta: number;
  valorMaderas: number;
  valorIndumentaria: number;
  medioDePago: string;
  total: number;
  pagado: boolean;
}

export class EstudianteExamen {
  constructor(
    // Reception data
    public nroLibreta: number,
    public apellido: string,
    public nombre: string,
    public idInstructor: number,
    public idGraduacionActual: number, // 0 = BLANCO, 1+ = actual graduations
    public idGraduacionExamen: number,
    public horaRegistro: Date = new Date(),
    
    // Control flags
    public nuevoEstudiante: boolean = false,
    public existeEnBD: boolean = false,
    public tomoExamen: boolean = false,
    public datosCoinciden: boolean = false,
    public examenesCompletos: boolean = false,
    
    // Warnings
    public advertencias: string[] = [],
    
    // Database comparison
    public datosBaseDatos?: Estudiante,
    public examenesFaltantes?: string[],
    
    // Observaciones field
    public observaciones?: string,
    
    // NEW: Payment information
    public pago: PagoEstudiante = {
      incluyeExamen: true,
      incluyeLibreta: false,
      maderasSeleccionadas: [],
      precioIndumentaria: 0,
      indumentariaDescripcion: '',
      descuentoPorcentaje: 0,
      descuentoValor: 0,
      descExamenPct: 0,
      descLibretaPct: 0,
      descMaderasPct: 0,
      descIndumentariaPct: 0,
      valorExamen: 0,
      valorLibreta: 0,
      valorMaderas: 0,
      valorIndumentaria: 0,
      medioDePago: 'Efectivo',
      total: 0,
      pagado: false
    }
  ) {}

  // Static method to create from reception form data
  static fromReceptionData(data: {
    nroLibreta: number;
    apellido: string;
    nombre: string;
    idInstructor: number;
    idGraduacionActual: number;
    idGraduacionExamen: number;
    pago?: PagoEstudiante;
  }): EstudianteExamen {
    const estudiante = new EstudianteExamen(
      data.nroLibreta,
      data.apellido,
      data.nombre,
      data.idInstructor,
      data.idGraduacionActual,
      data.idGraduacionExamen
    );
    
    // Add payment data if provided
    if (data.pago) {
      estudiante.pago = data.pago;
    }
    
    return estudiante;
  }

  // Method to update control status
  updateControlStatus(
    nuevoEstudiante: boolean,
    existeEnBD: boolean,
    tomoExamen: boolean,
    datosCoinciden: boolean,
    examenesCompletos: boolean,
    advertencias: string[] = [],
    examenesFaltantes: string[] = []
  ): void {
    this.nuevoEstudiante = nuevoEstudiante;
    this.existeEnBD = existeEnBD;
    this.tomoExamen = tomoExamen;
    this.datosCoinciden = datosCoinciden;
    this.examenesCompletos = examenesCompletos;
    this.advertencias = advertencias;
    this.examenesFaltantes = examenesFaltantes;
  }

  // Get status icon for table display
  getStatusIcon(): string {
    // Check if this is a new student based on either the flag or graduation status
    if (this.nuevoEstudiante || (this.idGraduacionActual === 0 && !this.existeEnBD)) return '🆕';
    if (!this.existeEnBD) return '❌';
    if (this.advertencias.length > 0) return '⚠️';
    if (this.tomoExamen && this.datosCoinciden && this.examenesCompletos) return '✅';
    return '🕐'; // Pending
  }

  // Get status message
  getStatusMessage(): string {
    if (this.idGraduacionActual < 1){
      this.advertencias = [];
      return 'Nuevo estudiante';
    } 
    if (!this.existeEnBD) return 'No registrado';
    if (!this.tomoExamen) return 'Examen pendiente';
    if (!this.datosCoinciden) return 'Datos no coinciden';
    if (!this.examenesCompletos) return 'Exámenes incompletos';
    return 'Examen completado';
  }

  // Helper method to get graduation description including BLANCO
  getGraduacionActualNombre(graduaciones: Graduacion[]): string {
    if (this.idGraduacionActual === 0) {
      return 'BLANCO';
    }
    
    const graduacion = graduaciones.find(g => g.id === this.idGraduacionActual);
    return graduacion ? graduacion.nombre : `ID: ${this.idGraduacionActual}`;
  }
}